import { useEffect, useRef, useState, useCallback } from "react";

// Type declarations for Web Speech API
interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
    onend: ((this: SpeechRecognition, ev: Event) => any) | null;
    onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
    onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

interface SpeechRecognitionEvent extends Event {
    resultIndex: number;
    results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
    error: string;
    message: string;
}

interface SpeechRecognitionResultList {
    length: number;
    item(index: number): SpeechRecognitionResult;
    [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
    length: number;
    item(index: number): SpeechRecognitionAlternative;
    [index: number]: SpeechRecognitionAlternative;
    isFinal: boolean;
}

interface SpeechRecognitionAlternative {
    transcript: string;
    confidence: number;
}

declare global {
    interface Window {
        SpeechRecognition: {
            new (): SpeechRecognition;
        };
        webkitSpeechRecognition: {
            new (): SpeechRecognition;
        };
    }
}

export interface UseSpeechRecognitionOptions {
    onTranscript?: (transcript: string) => void;
    onError?: (error: Error) => void;
    language?: string;
    continuous?: boolean;
    interimResults?: boolean;
}

export interface UseSpeechRecognitionReturn {
    isListening: boolean;
    transcript: string;
    error: Error | null;
    startListening: () => void;
    stopListening: () => void;
    isSupported: boolean;
}

/**
 * Hook for speech recognition using Web Speech API
 * 
 * Supports:
 * - Chrome, Edge, Safari (webkitSpeechRecognition)
 * - Fallback graceful per browser non supportati
 */
export function useSpeechRecognition(
    options: UseSpeechRecognitionOptions = {}
): UseSpeechRecognitionReturn {
    const {
        onTranscript,
        onError,
        language = "it-IT",
        continuous = true,
        interimResults = true,
    } = options;

    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [error, setError] = useState<Error | null>(null);
    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const finalTranscriptRef = useRef("");
    const isListeningRef = useRef(false); // Ref per accesso sincrono allo stato
    const onTranscriptRef = useRef(onTranscript); // Ref per callback sempre aggiornato

    // Check if Speech Recognition is supported
    const isSupported = typeof window !== "undefined" && (
        "SpeechRecognition" in window ||
        "webkitSpeechRecognition" in window
    );

    // Initialize Speech Recognition
    useEffect(() => {
        if (!isSupported) {
            setError(new Error("Speech recognition is not supported in this browser"));
            return;
        }

        const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = continuous;
        recognition.interimResults = interimResults;
        recognition.lang = language;

        recognition.onstart = () => {
            setIsListening(true);
            isListeningRef.current = true;
            setError(null);
            finalTranscriptRef.current = "";
        };

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            // Non processare risultati se l'ascolto è stato fermato (usa ref per accesso sincrono)
            if (!isListeningRef.current) {
                return;
            }
            
            let interimTranscript = "";

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscriptRef.current += transcript + " ";
                } else {
                    interimTranscript += transcript;
                }
            }

            const fullTranscript = finalTranscriptRef.current + interimTranscript;
            setTranscript(fullTranscript.trim());

            // Usa il ref del callback per evitare stale closure
            if (onTranscriptRef.current && isListeningRef.current) {
                onTranscriptRef.current(fullTranscript.trim());
            }
        };

        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
            const errorMessage = event.error || "Unknown error";
            const error = new Error(`Speech recognition error: ${errorMessage}`);
            setError(error);
            setIsListening(false);

            if (onError) {
                onError(error);
            }
        };

        recognition.onend = () => {
            setIsListening(false);
            isListeningRef.current = false;
        };

        recognitionRef.current = recognition;
        
        // Aggiorna il ref del callback quando cambia
        onTranscriptRef.current = onTranscript;

        return () => {
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch (e) {
                    // Ignore errors when stopping
                }
            }
        };
    }, [isSupported, continuous, interimResults, language, onTranscript, onError]);

    const startListening = useCallback(() => {
        if (!isSupported || !recognitionRef.current) {
            setError(new Error("Speech recognition is not available"));
            return;
        }

        try {
            recognitionRef.current.start();
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            setIsListening(false);
            if (onError) {
                onError(error);
            }
        }
    }, [isSupported, onError]);

    const stopListening = useCallback(() => {
        // Set immediately using ref to prevent further transcript updates (synchronous)
        isListeningRef.current = false;
        setIsListening(false);
        
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (err) {
                // Ignore errors when stopping
            }
        }
    }, []);

    return {
        isListening,
        transcript,
        error,
        startListening,
        stopListening,
        isSupported,
    };
}
